import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { UserContext } from 'context/user-context';

const GREEN = '#2DDE91';
const DARK = 'rgb(7,33,22)';

export default function ChatLauncher() {
  const { chat } = useContext(UserContext);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, loading, open]);

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || loading) return;

    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setDraft('');
    setError('');
    setLoading(true);

    try {
      const result = await chat(nextMessages);
      setMessages(result);
    } catch (err) {
      const message = err?.message || 'Could not reach the chat backend';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      {open && (
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            right: 24,
            bottom: 96,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            height: 460,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            backgroundColor: DARK,
            color: '#fff',
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <Typography sx={{ fontWeight: 'bold', color: GREEN }}>
              Alys
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOpen(false)}
              aria-label="Close chat"
              sx={{ color: '#fff' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box
            ref={listRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            {messages.length === 0 && !loading && (
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                Send a message to confirm the backend connection.
              </Typography>
            )}
            {messages.map((message, index) => (
              <Box
                key={`${message.role}-${index}`}
                sx={{
                  alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  backgroundColor:
                    message.role === 'user' ? GREEN : 'rgba(0,0,0,0.45)',
                  color: message.role === 'user' ? DARK : '#fff',
                }}
              >
                <Typography variant="body2">{message.content}</Typography>
              </Box>
            ))}
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <CircularProgress size={20} sx={{ color: GREEN }} />
              </Box>
            )}
            {error && (
              <Typography variant="body2" sx={{ color: '#ffb4a8' }}>
                {error}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              p: 1.5,
              display: 'flex',
              gap: 1,
              borderTop: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <TextField
              fullWidth
              size="small"
              placeholder="Type hello..."
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={onKeyDown}
              disabled={loading}
              inputProps={{ 'aria-label': 'Chat message' }}
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: 'rgba(255,255,255,0.08)',
                  color: '#fff',
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(255,255,255,0.6)',
                  opacity: 1,
                },
              }}
            />
            <IconButton
              onClick={sendMessage}
              disabled={loading || !draft.trim()}
              aria-label="Send message"
              sx={{ color: GREEN }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Paper>
      )}

      <Fab
        aria-label={open ? 'Close chat' : 'Open chat'}
        onClick={() => setOpen((value) => !value)}
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: 1300,
          backgroundColor: GREEN,
          color: DARK,
          '&:hover': {
            backgroundColor: '#24c57f',
          },
        }}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </Fab>
    </>
  );
}
